import axios from 'axios';
import NodeCache from 'node-cache';
import api from '@/utils/api';
import type { IAppointmentBookingData } from '@/store/types/IAppointment';

/*
  Clears cache after 1 hour
  This section initializes a new instance of NodeCache with a standard TTL (time to live) of 3600 seconds (1 hour) and sets deleteOnExpire to true, meaning cached items will be automatically removed after the TTL expires.
*/
const cache = new NodeCache({ stdTTL: 3600, deleteOnExpire: true });

/**
 * bookAppointment function
 * This function makes a POST request to the API to book an appointment.
 * It takes an object of type IAppointmentBookingData as a parameter and returns the promise from the axios.post call.
 *
 * @param {IAppointmentBookingData} data - The appointment booking data to be sent in the request body.
 * @returns {Promise} - The promise returned by axios.post.
 */
export function bookAppointment(data: IAppointmentBookingData) {
  return api.post('api/v1/appointment/', data);
}

/**
 * getCitiesData function
 * This function fetches cities data from an external API and caches the result for 1 hour.
 * It takes a boolean parameter 'isDev' to determine which company ID to use for the API call.
 *
 * @param {boolean} isDev - A flag indicating if the development environment should be used.
 * @returns {Promise} - The promise returned by the function, which resolves to the cities data.
 */
export async function getCitiesData(isDev) {
  const URL = `https://www.setster.com/api/v2/company/${isDev ? '17597' : '11735'}/location`; // Constructing the URL based on the 'isDev' flag

  // Checking if the cities data is already cached
  if (cache.has('setster_cities')) {
    return cache.get('setster_cities'); // Returning the cached data if available
  }

  // Making a GET request to fetch cities data
  const response = await axios.get(URL, {
    headers: {
      'Content-Type': 'application/json', // Setting the Content-Type header to application/json
    },
  });

  // Caching the response data for 1 hour
  cache.set('setster_cities', response.data);
  return response.data; // Returning the fetched cities data
}

/**
 * getSetsterAvailabilityDateTimes function
 * This function fetches availability date and times for a given service, location, and date range from Setster API.
 * It takes several parameters to construct the API URL and headers for the request.
 *
 * @param {string} serviceId - The ID of the service for which to fetch availability.
 * @param {string} locationId - The ID of the location for which to fetch availability.
 * @param {string} startDate - The start date for which to fetch availability.
 * @param {string} companyId - The ID of the company for which to fetch availability.
 * @param {string} timezone - The timezone for which to fetch availability.
 * @returns {Promise} - The promise returned by the function, which resolves to the availability data.
 */
export function getSetsterAvailabilityDateTimes(
  serviceId: string,
  locationId: string,
  startDate: string,
  companyId: string,
  timezone: string
) {
  const URL = `https://www.setster.com/api/v2/availability?company_id=${companyId}&service_id=${serviceId}&location_id=${locationId}&start_date=${startDate}&t=weekly&return=times&timezone_id=${timezone}&no_of_weeks=5`; // Constructing the URL with parameters

  // Making a GET request to fetch availability date and times
  return axios.get(URL, {
    headers: {
      'Content-Type': 'application/json', // Setting the Content-Type header to application/json
    },
  });
}

/**
 * getSetsterProviderId function
 * This function fetches the provider ID for a given service, location, datetime, and timezone from Setster API.
 * It takes several parameters to construct the API URL and headers for the request.
 *
 * @param {{ companyId: string; locationId: string; serviceId: string; datetime: string; timezoneId: string; }} params - An object containing the necessary parameters.
 * @returns {Promise} - The promise returned by the function, which resolves to the provider ID data.
 */
export function getSetsterProviderId({
  companyId,
  locationId,
  serviceId,
  datetime,
  timezoneId,
}: {
  companyId: string;
  locationId: string;
  serviceId: string;
  datetime: string;
  timezoneId: string;
}) {
  const URL = `https://www.setster.com/api/v2/company/${companyId}/availability?location_id=${locationId}&service_id=${serviceId}&get_provider=1&selected_time=${datetime}&timezone_id=${timezoneId}`; // Constructing the URL with parameters

  // Making a GET request to fetch provider ID
  return axios.get(URL, {
    headers: {
      'Content-Type': 'application/json', // Setting the Content-Type header to application/json
    },
  });
}
